import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Drawer,
  Row,
  Col,
  Select,
} from 'antd';

import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import { getSourceFieldMap, getType } from './workflowHelpers';

const { Option } = Select;

const JOURNAL_FIELDS = [
  {
    key: 'lineTable',
    label: 'Line Item Table',
    required: true,
    isTableSelect: true,
  },
  {
    key: 'amount',
    label: 'Amount',
    required: true,
    description: 'Line item amount',
    isTableColumn: true,
  },
  {
    key: 'projectId',
    label: 'Project',
    required: false,
    description: 'Project dropdown field from the form',
    isProjectField: true,
  },
  {
    key: 'description',
    label: 'Description',
    required: false,
    description: 'Line item description',
    isTableColumn: true,
    isTextFieldForEquipmentTable: true,
  },
];

function WorkflowSageIntacctCreateJournalConfigureDrawer({
  visible,
  onClose,
  onSubmit,
  sourceName,
  sourceSections = [],
  fieldMappings = {},
  onFieldMappingsChange,
  lineTableFields = [],
}) {
  const tableColumns = useMemo(() => {
    if (!fieldMappings.lineTable) return [];

    const selectedTable = sourceSections
      .flatMap((section) => section.fields || [])
      .find((field) => field.id === fieldMappings.lineTable);

    if (!selectedTable?.configProps?.columns) return [];

    return selectedTable.configProps.columns.map((column) => ({
      label: column.title || column.name,
      value: column.key,
    }));
  }, [fieldMappings.lineTable, sourceSections]);

  const sourceFieldMap = useMemo(() => getSourceFieldMap(sourceSections), [sourceSections]);

  const selectedLineTable = useMemo(() => {
    if (!fieldMappings.lineTable) return null;

    return sourceSections
      .flatMap((section) => section.fields || [])
      .find((field) => field.id === fieldMappings.lineTable);
  }, [fieldMappings.lineTable, sourceSections]);

  const isEquipmentTable = useMemo(() => (
    selectedLineTable && getType(selectedLineTable) === 'table - Equipment'
  ), [selectedLineTable]);

  const isTimeTrackingGrid = useMemo(() => (
    selectedLineTable && getType(selectedLineTable) === 'timeTrackingGrid'
  ), [selectedLineTable]);

  const projectDropdowns = sourceFieldMap['dropdown - Projects'] || [];
  const projectFields = projectDropdowns.map((field) => ({
    label: field.title,
    value: field.id,
  }));

  const textFields = (sourceFieldMap.text || []).map((field) => ({
    label: field.title,
    value: field.id,
  }));

  const equipmentDescriptionColumnOptions = useMemo(() => {
    if (!selectedLineTable?.configProps?.columns) return [];

    return selectedLineTable.configProps.columns
      .filter((column) => (
        column.key === 'notes'
        // isCustomText is for all custom fields (backwards compat)
        || (column.isCustomText && column.customDataType === 'text')
      ))
      .map((column) => ({
        label: column.name || column.title || column.key,
        value: column.key,
      }));
  }, [selectedLineTable]);

  const equipmentDescriptionFieldOptions = useMemo(() => [
    ...equipmentDescriptionColumnOptions,
    ...textFields.map((field) => ({
      label: `${field.label} (form field)`,
      value: field.value,
    })),
  ], [equipmentDescriptionColumnOptions, textFields]);

  const handleFieldChange = useCallback((fieldKey) => (value) => {
    const newMappings = { ...fieldMappings };
    if (value) {
      newMappings[fieldKey] = value;
    } else {
      delete newMappings[fieldKey];
    }
    if (fieldKey === 'lineTable') {
      delete newMappings.description;
      delete newMappings.amount;
    }
    onFieldMappingsChange(newMappings);
  }, [fieldMappings, onFieldMappingsChange]);

  return (
    <Drawer
      title={(
        <span>
          Configure which fields from this
          {' '}
          <b>{sourceName}</b>
          {' '}
          form are loaded into the Sage Intacct Journal Entry
        </span>
      )}
      visible={visible}
      onClose={onClose}
      width={600}
      footer={(
        <Row justify="end" gutter={10}>
          <Col>
            <OnTraccrButton
              title="Cancel"
              onClick={onClose}
            />
          </Col>
          <Col>
            <OnTraccrButton
              title="Done"
              type="primary"
              onClick={onSubmit}
            />
          </Col>
        </Row>
      )}
    >
      <div style={{ padding: '0 24px' }}>
        {JOURNAL_FIELDS.map((field) => {
          const {
            key,
            label,
            required,
            isTableSelect,
            isProjectField,
            isTableColumn,
            isTextFieldForEquipmentTable,
            description,
          } = field;

          if (isTextFieldForEquipmentTable && key === 'description' && isEquipmentTable) {
            return (
              <div key={key} style={{ marginBottom: 20 }}>
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontWeight: 'bold' }}>
                    {label}
                    {required && <span style={{ color: 'red' }}> *</span>}
                  </span>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                    Equipment table Notes or custom text column, or a form text field
                  </div>
                </div>
                <Select
                  value={fieldMappings[key]}
                  onChange={handleFieldChange(key)}
                  placeholder="Select column or text field"
                  style={{ width: '100%' }}
                  allowClear
                  showSearch
                  filterOption={(input, option) => (
                    (option?.label ?? option?.children ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  )}
                >
                  {equipmentDescriptionFieldOptions.map((option) => (
                    <Option key={option.value} value={option.value} label={option.label}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </div>
            );
          }

          if (isTableSelect) {
            return (
              <div key={key} style={{ marginBottom: 20 }}>
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontWeight: 'bold' }}>
                    {label}
                    {required && <span style={{ color: 'red' }}> *</span>}
                  </span>
                  {description && (
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                      {description}
                    </div>
                  )}
                </div>
                <Select
                  value={fieldMappings[key]}
                  onChange={handleFieldChange(key)}
                  placeholder={`Select ${label}`}
                  style={{ width: '100%' }}
                  allowClear
                >
                  {lineTableFields.map((tableField) => (
                    <Option key={tableField.value} value={tableField.value}>
                      {tableField.label}
                    </Option>
                  ))}
                </Select>
              </div>
            );
          }

          if (isProjectField) {
            return (
              <div key={key} style={{ marginBottom: 20 }}>
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontWeight: 'bold' }}>
                    {label}
                    {required && <span style={{ color: 'red' }}> *</span>}
                  </span>
                  {description && (
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                      {description}
                    </div>
                  )}
                </div>
                <Select
                  value={fieldMappings[key]}
                  onChange={handleFieldChange(key)}
                  placeholder={`Select ${label}`}
                  style={{ width: '100%' }}
                  allowClear
                  showSearch
                  filterOption={(input, option) => (
                    option.children.toLowerCase().includes(input.toLowerCase())
                  )}
                >
                  {projectFields.map((projectField) => (
                    <Option key={projectField.value} value={projectField.value}>
                      {projectField.label}
                    </Option>
                  ))}
                </Select>
              </div>
            );
          }

          if (!isTableColumn || !fieldMappings.lineTable) {
            return null;
          }

          if (key === 'description' && isEquipmentTable) {
            return null;
          }

          if (key === 'amount' && isTimeTrackingGrid) {
            return null;
          }

          return (
            <div key={key} style={{ marginBottom: 20 }}>
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 'bold' }}>
                  {label}
                  {required && <span style={{ color: 'red' }}> *</span>}
                </span>
                {description && (
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                    {description}
                  </div>
                )}
              </div>
              <Select
                value={fieldMappings[key]}
                onChange={handleFieldChange(key)}
                placeholder={`Select column for ${label}`}
                style={{ width: '100%' }}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {tableColumns.map((column) => (
                  <Option key={column.value} value={column.value}>
                    {column.label}
                  </Option>
                ))}
              </Select>
            </div>
          );
        })}
        {!fieldMappings.lineTable && (
          <div style={{
            padding: 20,
            backgroundColor: '#f5f5f5',
            borderRadius: 4,
            textAlign: 'center',
            color: '#888',
          }}>
            Please select a Line Item Table first to configure table field mappings
          </div>
        )}
      </div>
    </Drawer>
  );
}

WorkflowSageIntacctCreateJournalConfigureDrawer.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  sourceName: PropTypes.string.isRequired,
  sourceSections: PropTypes.array.isRequired,
  fieldMappings: PropTypes.object.isRequired,
  onFieldMappingsChange: PropTypes.func.isRequired,
  lineTableFields: PropTypes.array.isRequired,
};

WorkflowSageIntacctCreateJournalConfigureDrawer.defaultProps = {
  visible: false,
};

export default WorkflowSageIntacctCreateJournalConfigureDrawer;
